import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, withCtx as _withCtx, resolveDirective as _resolveDirective, withDirectives as _withDirectives, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "export-content__settings" }
const _hoisted_2 = { class: "export-content__setting" }
const _hoisted_3 = {
  key: 0,
  class: "export-content__step-hint"
}
const _hoisted_4 = {
  key: 1,
  class: "export-content__step-hint"
}
const _hoisted_5 = { class: "export-content__form--section" }
const _hoisted_6 = { class: "export-content__label-wrapper" }
const _hoisted_7 = { class: "export-content__form--section" }
const _hoisted_8 = { class: "export-content__submit-buttons" }
const _hoisted_9 = {
  key: 0,
  type: "submit",
  class: "btns export-content__button-readwise"
}
const _hoisted_10 = {
  key: 1,
  type: "submit",
  class: "btns export-content__button-readwise"
}

import { onBeforeMount, ref } from 'vue'
import { useWait } from '@/app/composables/useWait'

import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'
import FormError from '@/components/ui/FormError.vue'
import { ValidationState } from '@/helpers/form'

import DisablePopup from '@/app/components/export/DisablePopup.vue'
import InputWithContextMenu from '@/app/components/InputWithContextMenu.vue'
import { readwise } from '@/services/readwise'
import { assertNotNull } from '@/helpers/typing'


export default /*@__PURE__*/_defineComponent({
  __name: 'ReadwiseSettings',
  props: {
    isReadwiseEnabled: { type: Boolean, default: false }
  },
  emits: ["updateEnabled"],
  setup(__props: any, { emit: __emit }) {

const validation = ref(new ValidationState())
const token = ref<string | null>(null)
const view = ref(readwise)

const props = __props

const emit = __emit
const wait = useWait()

const popupDisable = ref<InstanceType<typeof DisablePopup> | null>(null)

onBeforeMount(async () => {
  await refresh()
  validation.value.reset()
})

async function refresh(): Promise<void> {
  token.value = await readwise.getToken()
  emit('updateEnabled', token.value !== null)
}

async function checkForm(): Promise<void> {
  if (!token.value || token.value.trim() === '') {
    validation.value.showMessage('readwise_token', 'Token may not be blank.')
    return
  }
  if (props.isReadwiseEnabled) {
    disableReadwise()
  } else {
    await enableReadwise()
  }
}

function getErrorMessage(error: any): string {
  // We already sent the error to the Sentry (see ReadwiseAPI._post).
  // Here we just show the error for a user.
  let errorMessage = 'Readwise integration error. Contact support, please.'

  // We can get error without response in case when the browser
  // received a 401 unauthorized response when doing the preflight
  // OPTION request, resulting in a Network Error.
  // See: https://stackoverflow.com/questions/49886315/axios-interceptors-response-undefined
  if (error.response === undefined) {
    errorMessage = 'Wrong Access Token: Authorization Failed.'
  }
  // Just in case added handling for 401 Authorization Error.
  // Not sure if this case will be at all or we always will
  // receive the previous case (an error without response),
  // but it is better to have this.
  if (error.response && error.response.status === 401) {
    errorMessage = 'Wrong Access Token: Authorization Failed.'
  }

  return errorMessage
}

async function enableReadwise(): Promise<void> {
  try {
    validation.value.reset()
    wait.start('Connecting')
    await readwise.enableAndExportAll(assertNotNull(token.value))
    emit('updateEnabled', true)
  } catch (error) {
    validation.value.showMessage('readwise_token', getErrorMessage(error))
  } finally {
    wait.end('Connecting')
  }
}

function disableReadwise(): void {
  popupDisable.value?.show()
}

return (_ctx: any,_cache: any) => {
  const _component_v_wait = _resolveComponent("v-wait")!
  const _directive_wait = _resolveDirective("wait")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (__props.isReadwiseEnabled)
        ? (_openBlock(), _createElementBlock("p", _hoisted_3, " Highlights export to Readwise is enabled. "))
        : (_openBlock(), _createElementBlock("p", _hoisted_4, [...(_cache[2] || (_cache[2] = [
            _createTextVNode(" To enable highlights export to Readwise, open the ", -1),
            _createElementVNode("a", {
              class: "export-content__link",
              href: "https://readwise.io/access_token",
              target: "_blank"
            }, "Readwise Access Token page", -1),
            _createTextVNode(", copy and paste the API token into the field below and click “Enable” button. ", -1)
          ]))])),
      _createElementVNode("form", {
        role: "form",
        class: "form export-content__form_readwise",
        novalidate: "",
        onSubmit: _withModifiers(checkForm, ["prevent"])
      }, [
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _cache[3] || (_cache[3] = _createElementVNode("label", {
              class: "export-content__label",
              for: "token"
            }, "API key", -1)),
            _createVNode(FormError, {
              errors: validation.value.errors,
              field: "readwise_token"
            }, null, 8, ["errors"])
          ]),
          _createVNode(InputWithContextMenu, {
            ref: "input",
            modelValue: token.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((token).value = $event)),
            name: "token",
            rows: "1",
            type: "text",
            class: _normalizeClass(["export-content__input", validation.value.stateClass('readwise_token')]),
            placeholder: "Readwise Token...",
            disabled: __props.isReadwiseEnabled
          }, null, 8, ["modelValue", "disabled", "class"])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _cache[8] || (_cache[8] = _createElementVNode("div", { class: "export-content__wrapper" }, [
            _createElementVNode("span", { class: "export-content__wrapper--text" }, " Integration allows exporting your highlights automatically. ")
          ], -1)),
          _createElementVNode("div", _hoisted_8, [
            (!__props.isReadwiseEnabled)
              ? _withDirectives((_openBlock(), _createElementBlock("button", _hoisted_9, [
                  _createVNode(_component_v_wait, { for: "Connecting" }, {
                    waiting: _withCtx(() => [
                      _cache[4] || (_cache[4] = _createTextVNode(" Connecting ", -1)),
                      _createVNode(ButtonSpinner)
                    ]),
                    default: _withCtx(() => [
                      _cache[5] || (_cache[5] = _createTextVNode(" Connect and Export ", -1))
                    ]),
                    _: 1
                  })
                ])), [
                  [_directive_wait, 'Connecting', "disabled"]
                ])
              : _createCommentVNode("", true),
            (__props.isReadwiseEnabled)
              ? _withDirectives((_openBlock(), _createElementBlock("button", _hoisted_10, [
                  _createVNode(_component_v_wait, { for: "Disconnecting" }, {
                    waiting: _withCtx(() => [
                      _cache[6] || (_cache[6] = _createTextVNode(" Disconnecting ", -1)),
                      _createVNode(ButtonSpinner)
                    ]),
                    default: _withCtx(() => [
                      _cache[7] || (_cache[7] = _createTextVNode(" Disconnect ", -1))
                    ]),
                    _: 1
                  })
                ])), [
                  [_directive_wait, 'Disconnecting', "disabled"]
                ])
              : _createCommentVNode("", true)
          ])
        ]),
        _createVNode(DisablePopup, {
          ref_key: "popupDisable",
          ref: popupDisable,
          "integration-name": "Readwise",
          disableIntegration: view.value.disableIntegration,
          afterDisableCallback: refresh,
          onUpdateEnabled: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('updateEnabled', false)))
        }, null, 8, ["disableIntegration"])
      ], 32)
    ])
  ]))
}
}

})